import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  BookOpen,
  CornerDownLeft,
  Hash,
  Home,
  Inbox,
  Loader2,
  PlusCircle,
  Search,
  SlidersHorizontal,
  UserCircle,
} from "lucide-react";
import { Permission, useCan, useSession } from "@/components/session-provider";
import { searchArticles, type ArticleListItem } from "@/lib/knowledge";
import {
  listTickets,
  lookupTicketByDisplayNumber,
  parseTicketQuery,
  useStatusLabel,
  type TicketSummary,
} from "@/lib/tickets";
import { cn } from "@/lib/utils";

/**
 * ⌘K / Ctrl-K.
 *
 * Ticket results come from the server, which means the palette can only ever surface
 * tickets the caller is allowed to see — the search runs inside the same query filter and
 * RLS as the queue. The mock searched a client-side array, which on real data would have
 * meant either shipping every ticket to the browser or searching only the page in hand.
 *
 * Typing a request number here goes to that request (D-33) — the same lookup the queue's
 * quick search runs, so "412" means one thing wherever it is typed.
 *
 * ⚠️ The lookup fires on ENTER, not while typing. Every resolved lookup is recorded as a
 * PHI read against the caller's name, so searching per keystroke would file reads of
 * tickets 4 and 41 on the way to 412 — audit entries naming a person who never asked to
 * see those requests. The palette therefore OFFERS the jump as you type and performs it
 * only when you commit to it.
 */
export function CommandPalette() {
  const navigate = useNavigate();
  const statusLabelFor = useStatusLabel();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TicketSummary[]>([]);
  const [looking, setLooking] = useState(false);
  const [missed, setMissed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [articles, setArticles] = useState<ArticleListItem[]>([]);

  const canReadTickets = useCan(Permission.TicketRead);
  const canCreate = useCan(Permission.TicketCreate);
  const canReadSettings = useCan(Permission.SettingsRead);

  // ⚠️ The session's own flag, never a permission. Reading an article is baseline and there
  // is no kb.read to ask for; what decides whether somebody has the knowledge base is the
  // rollout, and `canSeeKnowledgeBase` is computed server-side from the same expression every
  // reader route runs (D-51's lesson about flags that look interchangeable).
  const { user } = useSession();
  const canSeeKnowledgeBase = user?.canSeeKnowledgeBase === true;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }

      if (e.key === "Escape") setOpen(false);
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      return;
    }

    setQuery("");
    setMissed(false);
    setArticles([]);
  }, [open]);

  /**
   * Articles matching what has been typed.
   *
   * ⚠️ **Per keystroke here, and the ticket lookup above is deliberately not** — the asymmetry
   * is the audit trail rather than the cost. Every ticket a search returns files a PHI read
   * against the caller's name, so a live one records reads of requests 4 and 41 on the way to
   * 412 (D-33, D-78). An article search returns organisation content and audits nothing, which
   * is the decision the reader route records: a per-read event on a how-to guide would be the
   * read receipt this register has refused four times.
   *
   * ⚠️ A failure clears the list rather than surfacing, because the palette already has two
   * other things to say and a search box that has found nothing yet is the ordinary state. The
   * screen at /kb is where a failed search says so.
   */
  useEffect(() => {
    if (!open || !canSeeKnowledgeBase) return;

    const term = query.trim();

    // The server refuses anything shorter with a message; asking would spend a rate-limit
    // permit to be told what is already known here.
    if (term.length < 3) {
      setArticles([]);
      return;
    }

    const handle = setTimeout(() => {
      void searchArticles(term)
        .then(setArticles)
        .catch(() => setArticles([]));
    }, 200);

    return () => clearTimeout(handle);
  }, [open, query, canSeeKnowledgeBase]);

  // Debounced, so typing does not fire a request per keystroke against a PHI-bearing
  // endpoint that audits every ticket it returns.
  useEffect(() => {
    if (!open || !canReadTickets) return;

    const handle = setTimeout(() => {
      void listTickets({ openOnly: true, pageSize: 5, sortBy: "UpdatedAt" })
        .then((page) => setResults(page.items))
        .catch(() => setResults([]));
    }, 200);

    return () => clearTimeout(handle);
  }, [open, query, canReadTickets]);

  if (!open) return null;

  // What the typed text is, if it is an identifier at all. Recomputed per render rather
  // than stored: it is a pure function of the query, and a second copy in state is a
  // second thing to keep in step with it.
  const identifier = canReadTickets ? parseTicketQuery(query) : null;

  /**
   * Goes to the request the query names.
   *
   * ⚠️ Failure says "no request matches", never "you cannot see that one". The server
   * returns the same 404 for a number that was never issued and one belonging to somebody
   * else, precisely so the two cannot be told apart — reporting the difference here would
   * rebuild the enumeration oracle in the browser.
   */
  async function jumpToTicket() {
    if (!identifier || looking) return;

    setMissed(false);

    if (identifier.kind === "number") {
      setOpen(false);
      void navigate({ to: "/tickets/$id", params: { id: identifier.value } });
      return;
    }

    setLooking(true);
    const found = await lookupTicketByDisplayNumber(identifier.value);
    setLooking(false);

    if (!found) {
      setMissed(true);
      return;
    }

    setOpen(false);
    void navigate({ to: "/tickets/$id", params: { id: found.number } });
  }

  const actions = [
    { label: "Home", icon: Home, to: "/", show: true },
    { label: "Get help", icon: PlusCircle, to: "/new", show: canCreate },
    { label: "Tickets", icon: Inbox, to: "/tickets", show: canReadTickets },
    { label: "Settings", icon: SlidersHorizontal, to: "/settings", show: canReadSettings },
    { label: "Profile", icon: UserCircle, to: "/profile", show: true },
  ].filter((a) => a.show);

  const visible = query
    ? actions.filter((a) => a.label.toLowerCase().includes(query.toLowerCase()))
    : actions;

  function go(to: string) {
    setOpen(false);
    void navigate({ to });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[10vh]"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-xl"
      >
        {/* A form, so Enter commits the lookup instead of doing nothing. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void jumpToTicket();
          }}
          className="flex items-center gap-2 border-b border-border px-3"
        >
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setMissed(false);
            }}
            placeholder={canReadTickets ? "Jump to… or a request number" : "Jump to…"}
            aria-label="Search"
            autoComplete="off"
            className="h-12 w-full bg-transparent text-base outline-none md:text-sm"
          />
        </form>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {identifier && (
            <button
              type="button"
              onClick={() => void jumpToTicket()}
              className="flex w-full items-center gap-2.5 rounded-lg bg-accent/60 px-2 py-2 text-left text-sm transition hover:bg-accent"
            >
              {looking ? (
                <Loader2
                  className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"
                  aria-hidden
                />
              ) : (
                <Hash className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              )}

              <span className="min-w-0 flex-1">
                Go to request{" "}
                <span className="font-medium">
                  {identifier.kind === "displayNumber" ? `#${identifier.value}` : identifier.value}
                </span>
              </span>

              <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            </button>
          )}

          {missed && (
            <p role="status" className="px-2 py-2 text-sm text-muted-foreground">
              No request matches that number.
            </p>
          )}

          {visible.length > 0 && (
            <ul>
              {visible.map((a) => {
                const Icon = a.icon;
                return (
                  <li key={a.to}>
                    <button
                      type="button"
                      onClick={() => go(a.to)}
                      className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm transition hover:bg-accent"
                    >
                      <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                      {a.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {canReadTickets && results.length > 0 && (
            <>
              <p className="px-2 pb-1 pt-3 text-xs uppercase tracking-wide text-muted-foreground">
                Recently updated
              </p>
              <ul>
                {results.map((t) => (
                  <li key={t.number}>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        void navigate({ to: "/tickets/$id", params: { id: t.number } });
                      }}
                      className="flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left text-sm transition hover:bg-accent"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-1 block">{t.subject}</span>
                        <span className="block text-xs text-muted-foreground">
                          {/* The readable number, matching the queue and every email. */}#
                          {t.displayNumber} · {statusLabelFor(t.status)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {articles.length > 0 && (
            <>
              <p className="px-2 pb-1 pt-3 text-xs uppercase tracking-wide text-muted-foreground">
                Knowledge base
              </p>
              <ul>
                {articles.map((article) => (
                  <li key={`${article.collectionSlug}/${article.slug}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        void navigate({
                          to: "/kb/$collection/$slug",
                          params: { collection: article.collectionSlug, slug: article.slug },
                        });
                      }}
                      className="flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left text-sm transition hover:bg-accent"
                    >
                      <BookOpen
                        className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-1 block">{article.title}</span>
                        <span className="line-clamp-1 block text-xs text-muted-foreground">
                          {article.collectionName}
                          {article.summary === null ? "" : ` · ${article.summary}`}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Not shown when a jump is on offer or a lookup has just missed — both already
              say what happened, and "Nothing matches" underneath either one contradicts
              the row above it. */}
          {visible.length === 0 &&
            results.length === 0 &&
            articles.length === 0 &&
            !identifier &&
            !missed && (
              <p className={cn("px-2 py-6 text-center text-sm text-muted-foreground")}>
                Nothing matches.
              </p>
            )}
        </div>
      </div>
    </div>
  );
}
